import * as React from 'react';
import { Icon as LucideIconBase, type IconNode } from 'lucide-react';
// NOTE: import the dynamic entry ONLY — `lucide-react/dynamicIconImports`
// resolves to a raw ESM file that Node's CJS interop cannot parse in CI
// vitest, and `lucide-react/dynamic` is what the vitest stub alias
// intercepts (see packages/hen-ui/vitest.config.ts).
import {
  DynamicIcon,
  dynamicIconImports,
  type IconName as LucideDynamicIconName,
} from 'lucide-react/dynamic';

import {
  useIconRegistry,
  type IconRegistryEntry,
} from '@/hen-ui/providers/IconRegistry';
import {
  iconBackgroundContainerVariants,
  iconVariants,
  isIconSolidFill,
  type IconVariantProps,
} from './icon.variants';
import { resolveIconSource } from './resolve-icon';
import { getActivatableProps } from '@/hen-ui/lib/activatable';
import { cn, warnOnce } from '@/hen-ui/utils';

/**
 * Type for valid Lucide icon names.
 * This is a union of all valid Lucide icon names exported from lucide-react/dynamic.
 * DynamicIcon will handle invalid names gracefully.
 *
 * @example
 * // Valid icon names: 'check', 'x', 'arrow-right', 'heart', 'arrow-up-circle', etc.
 * <Icon icon="check" />
 * <Icon icon="arrow-up-circle" />
 */
export type LucideIconName = LucideDynamicIconName;

/**
 * Namespaced pack id. Hosts inject the matching SVG via {@link IconRegistry}.
 * `custom` is a literal prefix meaning “not Lucide,” not the publisher name.
 * Never a Lucide kebab and never a URL.
 */
export type CustomIconId = `custom/${string}`;

/**
 * Values accepted by {@link Icon} `icon` and every composite Icon slot.
 * Lucide kebab (default catalog) or a host-injected `custom/{slug}`.
 */
export type IconName = LucideIconName | CustomIconId;

/**
 * Module-level cache of loaded Lucide icon nodes, keyed by icon name.
 *
 * Why this exists: lucide's `DynamicIcon` starts with empty state on
 * EVERY mount and async-imports the SVG (`useState()` + `useEffect`),
 * rendering `null` until the import resolves. Any remount — e.g. a DSL
 * tree re-rendering on formState changes — replays that empty→loaded
 * cycle, so icons visibly flash and reflow their siblings. Caching the
 * resolved `IconNode` here lets every mount after the first render the
 * SVG synchronously, while keeping the lazy per-icon import (the full
 * Lucide set is never bundled).
 */
const iconNodeCache = new Map<LucideIconName, IconNode>();

function loadIconNode(name: LucideIconName): Promise<IconNode | undefined> {
  const importer = dynamicIconImports[name];
  if (!importer) {
    console.error(`[hen-ui] Icon "${name}" is not a valid Lucide icon name.`);
    return Promise.resolve(undefined);
  }
  // Same module contract `DynamicIcon` relies on: each dynamic icon
  // module exposes its render tree as the `__iconNode` named export.
  return (importer() as Promise<{ __iconNode?: IconNode }>).then((mod) => {
    if (mod.__iconNode) {
      iconNodeCache.set(name, mod.__iconNode);
    }
    return mod.__iconNode;
  });
}

/** Pointer cursor and focus ring for a bound icon. Same ring tokens as Link. */
const BOUND_ICON_CLASS_NAME =
  'cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-focus focus-visible:ring-offset-1 focus-visible:ring-offset-focus';

const MONO_MASK_CLASSES = 'block h-full w-full';

// Registry and empty marks are `<span>`s carrying `data-hen-icon`. Parents
// that size or color icons through `svg` descendant selectors must also
// match `[data-hen-icon]`, or pack icons drift from Lucide ones.
const REGISTRY_MARK = 'custom';

// Paint properties are inline because host apps compile hen-ui classes with
// their own Tailwind config, which may not emit `bg-current` or arbitrary
// properties. Forced-colors mode repaints backgrounds, which would erase a
// mask painted with `background-color`; the inherited `color` is still the
// forced one.
function monoMaskStyle(url: string): React.CSSProperties {
  const image = `url("${url}")`;
  return {
    backgroundColor: 'currentColor',
    forcedColorAdjust: 'none',
    printColorAdjust: 'exact',
    WebkitPrintColorAdjust: 'exact',
    maskImage: image,
    WebkitMaskImage: image,
    maskSize: 'contain',
    WebkitMaskSize: 'contain',
    maskRepeat: 'no-repeat',
    WebkitMaskRepeat: 'no-repeat',
    maskPosition: 'center',
    WebkitMaskPosition: 'center',
  };
}

type SpanPassthrough = Pick<
  React.HTMLAttributes<HTMLSpanElement>,
  'id' | 'style'
> &
  Record<`data-${string}`, unknown>;

/**
 * Props that mean the same on the `<span>` drawn for registry and empty marks.
 * SVG presentation attributes and SVG-typed handlers are Lucide-only.
 */
function pickSpanPassthrough(props: Record<string, unknown>): SpanPassthrough {
  const passthrough: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(props)) {
    if (key === 'id' || key === 'style' || key.startsWith('data-')) {
      passthrough[key] = value;
    }
  }
  return passthrough as SpanPassthrough;
}

function RegistryMark({
  entry,
  className,
  ariaHidden,
  ariaLabel,
  passthrough,
  triggerProps,
}: {
  entry: IconRegistryEntry;
  className: string;
  ariaHidden: React.AriaAttributes['aria-hidden'];
  ariaLabel?: string;
  passthrough: SpanPassthrough;
  /** Button semantics when the mark itself carries the click action. */
  triggerProps?: React.HTMLAttributes<HTMLSpanElement>;
}): React.ReactElement {
  const hidden = ariaHidden === true || ariaHidden === 'true';
  const labeled = Boolean(ariaLabel) && !hidden;

  if (entry.colorMode === 'original') {
    return (
      <span
        {...passthrough}
        className={className}
        data-hen-icon={REGISTRY_MARK}
        data-hen-icon-color='original'
        aria-hidden={hidden}
        {...triggerProps}
      >
        <img
          src={entry.url}
          alt={labeled ? ariaLabel : ''}
          className='block h-full w-full object-contain'
          decoding='async'
          draggable={false}
        />
      </span>
    );
  }

  return (
    <span
      {...passthrough}
      className={className}
      data-hen-icon={REGISTRY_MARK}
      data-hen-icon-color='mono'
      aria-hidden={hidden}
      {...(labeled ? { 'aria-label': ariaLabel, role: 'img' } : {})}
      {...triggerProps}
    >
      <span className={MONO_MASK_CLASSES} style={monoMaskStyle(entry.url)} />
    </span>
  );
}

/**
 * Props for the Icon component.
 * Extends standard SVG element props and supports accessibility attributes.
 *
 * @remarks
 * - `aria-label`: Accessible label for semantic icons. Required if the icon is not decorative.
 * - `aria-hidden`: Whether the icon is decorative. Defaults to false. Set to true for decorative icons.
 * - Registry (`custom/*`) and empty marks render a `<span>` that receives only
 *   `id`, `style`, and `data-*`. `ref`, SVG presentation props, and event
 *   handlers other than a click action apply to Lucide glyphs. A click action
 *   (`onClick`, with `onKeyDown` / `onKeyUp`) applies to whichever element
 *   becomes the button.
 */
export interface IconProps
  extends Omit<React.SVGProps<SVGSVGElement>, 'size' | 'color' | 'name'>,
    IconVariantProps {
  /**
   * Lucide kebab name, or a host-injected `custom/{slug}` when wrapped in
   * {@link IconRegistry}. Authors pick Lucide; registry ids are not a
   * catalog upload field.
   * @example
   * <Icon icon="check" />
   * <Icon icon="custom/badge-check" />
   */
  icon: IconName;
  /**
   * Reserve the padded background box even when `background` is `default`.
   * Keeps the SVG at its requested size while background chrome changes.
   */
  reserveBackground?: boolean;
}

/**
 * Icon component following HEN Design System standards.
 *
 * A standardized wrapper for Lucide icons that applies HEN Design System tokens
 * for consistent sizing, coloring, and spacing. Hosts may wrap the tree in
 * {@link IconRegistry} so `custom/{slug}` ids load a pack glyph by URL
 * (`mono` as a `currentColor` mask, `original` as an image).
 *
 * @example
 * // Basic usage with default size and color
 * <Icon icon="check-circle" />
 *
 * @example
 * // With size and color variants
 * <Icon icon="arrow-right" size="lg" color="primary" />
 *
 * @example
 * // As a semantic icon with accessibility
 * <Icon icon="check" aria-label="Item completed" aria-hidden="false" />
 *
 * @example
 * // As a decorative icon (default)
 * <Icon icon="clipboard" aria-hidden="true" />
 *
 * @example
 * // With custom className using design tokens
 * <Icon icon="arrow-down" className="text-primary-700" />
 *
 * @example
 * // With a click action the icon becomes a keyboard-operable button named by
 * // `aria-label`, even if it was marked decorative.
 * <Icon icon="info" aria-label="More information" onClick={openDetails} />
 */
export const Icon = React.forwardRef<SVGSVGElement, IconProps>(
  (
    {
      icon,
      size,
      color,
      background,
      shape,
      reserveBackground = false,
      className,
      'aria-label': ariaLabel,
      'aria-hidden': ariaHidden = false,
      strokeWidth = 1,
      onClick,
      onKeyDown,
      onKeyUp,
      ...props
    },
    ref,
  ) => {
    const isBound = onClick != null;
    if (
      process.env.NODE_ENV !== 'production' &&
      isBound &&
      !ariaLabel?.trim()
    ) {
      warnOnce(
        'Icon/click-action-without-label',
        '[hen-ui/Icon] An Icon with a click action has no `aria-label`, so its button has no accessible name. Set `aria-label`.',
      );
    }
    // A reserved background box is the outermost element, so it takes the
    // button role and label, and the glyph inside it stays decorative.
    const backgroundIsTrigger = isBound && reserveBackground;
    const glyphIsTrigger = isBound && !reserveBackground;
    const eventProps = {
      onClick,
      ...(isBound
        ? getActivatableProps({ onClick, onKeyDown, onKeyUp })
        : { onKeyDown, onKeyUp }),
    };
    // Consumer handlers are typed for the svg host; on a span host they
    // receive that span's events instead.
    const spanEventProps =
      eventProps as unknown as React.HTMLAttributes<HTMLSpanElement>;
    const registry = useIconRegistry();
    const resolved = resolveIconSource(icon, registry);
    const lucideName = resolved.kind === 'lucide' ? resolved.name : undefined;

    // Seed from the module cache so remounts of an already-loaded icon
    // render the SVG synchronously — no empty frame, no sibling reflow.
    const [iconNode, setIconNode] = React.useState<IconNode | undefined>(() =>
      lucideName ? iconNodeCache.get(lucideName) : undefined,
    );

    React.useEffect(() => {
      if (!lucideName) {
        setIconNode(undefined);
        return;
      }
      const cached = iconNodeCache.get(lucideName);
      if (cached !== undefined) {
        setIconNode((current) => (current === cached ? current : cached));
        return;
      }
      let active = true;
      void loadIconNode(lucideName).then((node) => {
        if (active && node) {
          setIconNode(node);
        }
      });
      return () => {
        active = false;
      };
    }, [lucideName]);

    // Only include aria-label if provided and icon is not hidden
    // (aria-hidden={true} means the icon is decorative and shouldn't have a label)
    const glyphHidden = backgroundIsTrigger
      ? true
      : isBound
        ? false
        : ariaHidden;
    const ariaAttributes = {
      'aria-hidden': glyphHidden,
      ...(ariaLabel && !glyphHidden ? { 'aria-label': ariaLabel } : {}),
    };

    // Solid fills own the glyph color, so an authored `color` is dropped.
    const glyphColor = isIconSolidFill(background) ? null : color;

    const classes = cn(
      iconVariants({
        size,
        color: glyphColor,
        background: reserveBackground ? 'default' : background,
        shape,
      }),
      glyphIsTrigger && BOUND_ICON_CLASS_NAME,
      className,
    );
    const glyphEventProps = backgroundIsTrigger ? {} : eventProps;
    const markTriggerProps: React.HTMLAttributes<HTMLSpanElement> | undefined =
      glyphIsTrigger
        ? {
            ...spanEventProps,
            'aria-hidden': false,
            ...(ariaLabel ? { 'aria-label': ariaLabel } : {}),
          }
        : undefined;

    // Size-reserving fallback for the one genuine first load of an icon
    // name: same variant classes → same box, so the initial swap-in does
    // not reflow neighboring content.
    const Fallback = React.useCallback(
      () => <span className={classes} aria-hidden />,
      [classes],
    );

    let iconElement: React.ReactElement;
    if (resolved.kind === 'empty') {
      iconElement = (
        <span
          {...pickSpanPassthrough(props)}
          className={classes}
          aria-hidden
          data-hen-icon='empty'
          {...markTriggerProps}
        />
      );
    } else if (resolved.kind === 'registry') {
      iconElement = (
        <RegistryMark
          entry={resolved.entry}
          className={classes}
          ariaHidden={glyphHidden}
          ariaLabel={ariaLabel}
          passthrough={pickSpanPassthrough(props)}
          triggerProps={markTriggerProps}
        />
      );
    } else if (iconNode === undefined) {
      // First-ever render of this icon name in this JS context lets
      // DynamicIcon do the initial resolution while the effect above warms
      // the module cache so every later mount is synchronous.
      iconElement = (
        <DynamicIcon
          name={resolved.name}
          ref={ref}
          strokeWidth={strokeWidth}
          fallback={Fallback}
          className={classes}
          {...ariaAttributes}
          {...props}
          {...glyphEventProps}
        />
      );
    } else {
      iconElement = (
        <LucideIconBase
          iconNode={iconNode}
          ref={ref}
          strokeWidth={strokeWidth}
          className={classes}
          {...ariaAttributes}
          {...props}
          {...glyphEventProps}
        />
      );
    }

    if (!reserveBackground) return iconElement;

    const backgroundClassName = iconBackgroundContainerVariants({
      size,
      background,
      shape,
    });

    return (
      <span
        data-slot='icon-background'
        className={
          backgroundIsTrigger
            ? cn(backgroundClassName, BOUND_ICON_CLASS_NAME)
            : backgroundClassName
        }
        {...(backgroundIsTrigger
          ? {
              ...spanEventProps,
              ...(ariaLabel ? { 'aria-label': ariaLabel } : {}),
            }
          : {})}
      >
        {iconElement}
      </span>
    );
  },
);

Icon.displayName = 'Icon';
